import {
  AUTHENTICATOR_PROVIDER_KEYS,
  PAYROLL_CONSOLE_PROVIDER_KEYS,
  type PayrollConsoleProvider,
} from '@/external/db/types';
import { type ZodType, z } from 'zod';

// Universal API route definitions. Importable from both server and client —
// no server-only deps (no async_hooks, no DB). The server route framework
// lives in api-server.ts; the client fetch helper lives in api-client.ts.

// ============================================================================
// Route definitions
// ============================================================================

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type Audience = 'user' | 'participant' | 'back_office_admin' | 'public' | 'webhook';

interface RouteDefBase<TParams, TQuery, TBody> {
  method: HttpMethod;
  path: string;
  audience: Audience;
  params?: ZodType<TParams>;
  query?: ZodType<TQuery>;
  body?: ZodType<TBody>;
}

// `output` is "what comes out of the route." For JSON / raw / redirect
// routes, it's the response body type. For SSE, it's the per-event
// type. One generic covers both because a def is exactly one or the
// other (never both).
type RouteDefShape<TParams, TQuery, TBody, TOutput> = RouteDefBase<TParams, TQuery, TBody> &
  ({ output: ZodType<TOutput>; events?: never } | { output?: never; events: ZodType<TOutput> });

export type RouteDef<TParams = never, TQuery = never, TBody = never, TOutput = never> = RouteDefShape<
  TParams,
  TQuery,
  TBody,
  TOutput
>;

export function defineRouteDef<T extends RouteDefShape<unknown, unknown, unknown, unknown>>(def: T): T {
  return def;
}

// Non-JSON handler return (binary / CSV / XML). Pair with
// `output: rawDataOutput` on the def.
export class RawDataResponse {
  constructor(
    public readonly body: Buffer | Uint8Array | ReadableStream<Uint8Array> | string,
    public readonly contentType: string,
    public readonly headers?: Record<string, string>,
  ) {}
}

// Marker for raw-data routes. `callApi` checks `def.output instanceof
// RawDataOutputSchema` to skip JSON parsing and return the raw Response.
export class RawDataOutputSchema {
  declare readonly _output: RawDataResponse;
}

export const rawDataOutput = new RawDataOutputSchema() as RawDataOutputSchema & ZodType<RawDataResponse>;

// Returned from `resolveWorkspaceId` to ack-and-drop without running
// the handler. Class instance (not `{skip: T}`) so a `value` of
// `undefined` doesn't collide with the "no skip" branch.
export class SkipResponse<T> {
  constructor(public readonly value: T) {}
}

export class ResponseWithCookies<T> {
  constructor(
    public readonly data: T,
    public readonly cookies: ResponseCookieChange,
  ) {}
}

export interface ResponseCookieSet {
  name: string;
  value: string;
  options?: {
    httpOnly?: boolean;
    secure?: boolean;
    sameSite?: 'lax' | 'strict' | 'none';
    path?: string;
    maxAge?: number;
    expires?: Date;
    domain?: string;
  };
}

export interface ResponseCookieClear {
  name: string;
  path?: string;
}

export interface ResponseCookieChange {
  set?: ResponseCookieSet[];
  clear?: ResponseCookieClear[];
}

export class RedirectResponse {
  constructor(
    public readonly location: string,
    public readonly cookies?: ResponseCookieChange,
    public readonly status: 302 | 307 | 308 = 302,
  ) {}
}

export { z };

// ============================================================================
// SSE event schemas (per route)
// ============================================================================

// Matilda chat-turn events. Emitted by /api/chat, /api/chat/confirm,
// /api/global-chat. Kept as one union because the front-end consumer
// (ChatPanel + GlobalChatPanel) handles the same vocabulary.
export const chatEvent = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('agent'), handle: z.string(), display_name: z.string() }),
  z.object({ kind: z.literal('delta'), text: z.string() }),
  // An ephemeral "working on it" line shown before the first real token. Not part
  // of the persisted message — the client drops it as soon as the answer streams.
  z.object({ kind: z.literal('ack'), text: z.string() }),
  z.object({
    kind: z.literal('pending_confirm'),
    pending_call_id: z.string(),
    tool_name: z.string(),
    summary: z.string(),
  }),
  z.object({
    kind: z.literal('tool_use'),
    tool_use_id: z.string(),
    name: z.string(),
    args: z.unknown().optional(),
  }),
  z.object({
    kind: z.literal('tool_result'),
    tool_use_id: z.string(),
    name: z.string().optional(),
    ok: z.boolean().optional(),
    meta: z.object({ subagent_id: z.string() }).optional(),
  }),
  z.object({
    kind: z.literal('recommendation'),
    recommendation: z.enum(['confirm', 'reject']),
    pending_call_id: z.string(),
  }),
  z.object({ kind: z.literal('refresh') }),
  z.object({ kind: z.literal('done') }),
  z.object({ kind: z.literal('error'), message: z.string() }),
]);
export type ChatEvent = z.infer<typeof chatEvent>;

// /api/onboarding/client/[token]/chat — text streams in, that's it.
// No `done` (generator return signals end) and no `error` (framework
// errors come through SSE's `event: error` channel as a thrown
// StreamError — see api-client.ts).
export const onboardingClientChatEvent = z.object({
  kind: z.literal('delta'),
  text: z.string(),
});
export type OnboardingClientChatEvent = z.infer<typeof onboardingClientChatEvent>;

// /api/pipeline/run — per-stage progress + FTW session bracketing.
// `gap` lands here because the route is bus-forwarding from `pipelineQueue`
// (server-emitted when retained entries don't cover a `?since=N` resume).
export const pipelineRunEvent = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('log'), agent: z.string(), body: z.string() }),
  z.object({
    kind: z.literal('session_started'),
    agent: z.string(),
    session_id: z.string(),
    stage_key: z.string(),
    label: z.string(),
  }),
  z.object({
    kind: z.literal('session_ended'),
    agent: z.string(),
    session_id: z.string(),
    stage_key: z.string(),
    status: z.string(),
    steps: z.number(),
    failure_reason: z.string().nullable().optional(),
  }),
  z.object({ kind: z.literal('done'), ftw: z.record(z.string(), z.unknown()) }),
  z.object({ kind: z.literal('error'), message: z.string() }),
  z.object({ kind: z.literal('auth_required') }),
  z.object({ kind: z.literal('gap'), count: z.number().int().nullable() }),
]);
export type PipelineRunEvent = z.infer<typeof pipelineRunEvent>;

// /api/ftw/console/run — ad-hoc FTW agent runner.
export const ftwConsoleRunEvent = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('log'), body: z.string() }),
  z.object({ kind: z.literal('auth_required') }),
  z.object({
    kind: z.literal('session_started'),
    session_id: z.string(),
    stage_key: z.string(),
  }),
  z.object({
    kind: z.literal('action'),
    row: z.object({
      step_number: z.number(),
      occurred_at: z.string(),
      action_type: z.string(),
      page_url: z.string(),
      selector: z.string().nullable(),
      payload: z.unknown(),
      notes: z.string().nullable(),
      screenshot_url: z.string().nullable(),
    }),
  }),
  z.object({ kind: z.literal('assistant_text'), text: z.string() }),
  z.object({
    kind: z.literal('session_ended'),
    session_id: z.string(),
    status: z.enum(['completed', 'failed', 'aborted']),
    steps_executed: z.number(),
    output: z.record(z.string(), z.unknown()).nullable(),
    output_text: z.string(),
    failure_reason: z.string().nullable(),
  }),
  z.object({ kind: z.literal('error'), message: z.string() }),
]);
export type FtwConsoleRunEvent = z.infer<typeof ftwConsoleRunEvent>;

// /api/browser-computer-use/run — generic computer-use agent runner.
// Mirrors the FTW console event union but the action row is simpler
// (no FTW-specific fields) and a download event surfaces files the
// agent captured during the run.
export const bcuRunEvent = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('log'), body: z.string() }),
  z.object({
    kind: z.literal('session_started'),
    session_id: z.string(),
  }),
  z.object({
    kind: z.literal('action'),
    row: z.object({
      step_number: z.number(),
      occurred_at: z.string(),
      action_type: z.string(),
      page_url: z.string(),
      payload: z.unknown(),
      screenshot_url: z.string().nullable(),
    }),
  }),
  z.object({
    kind: z.literal('download'),
    row: z.object({
      id: z.string(),
      suggested_filename: z.string(),
      source_url: z.string(),
      byte_size: z.number(),
      captured_at: z.string(),
    }),
  }),
  z.object({ kind: z.literal('assistant_text'), text: z.string() }),
  z.object({
    kind: z.literal('session_ended'),
    session_id: z.string(),
    status: z.enum(['completed', 'failed', 'aborted']),
    steps_executed: z.number(),
    output_text: z.string(),
    failure_reason: z.string().nullable(),
  }),
  z.object({ kind: z.literal('error'), message: z.string() }),
]);
export type BcuRunEvent = z.infer<typeof bcuRunEvent>;

// The payroll providers an operator-driven console pull supports. The closed set
// lives in db/types; this binds the route body, the job schema, and the UI to it.
export const payrollConsoleProvider = z.enum(PAYROLL_CONSOLE_PROVIDER_KEYS);
// Authenticator bindings cover every system whose sign-in a TOTP code can
// answer, which is a superset of the payroll consoles.
export const authenticatorProvider = z.enum(AUTHENTICATOR_PROVIDER_KEYS);
export type { PayrollConsoleProvider };

// The outcome of a console payroll pull, after the downloaded report is parsed
// and its pay runs written. `unmatched_lines` is load-bearing, not decoration:
// a line that resolves to no participant still lands as a fact but is invisible
// to per-participant testing, so the operator has to see the count.
export const payrollConsoleSummary = z.object({
  payrolls: z.number(),
  employees: z.number(),
  lines: z.number(),
  unmatched_lines: z.number(),
  totals: z.object({
    gross: z.number(),
    pretax: z.number(),
    roth: z.number(),
    // Every employer dollar together: neither provider says whether its company
    // contribution is match or nonelective, so the console never claims one.
    employer: z.number(),
  }),
  // The captured browser download, served by
  // /api/browser-computer-use/download/[id] behind the "Download file" button.
  download_id: z.string().nullable(),
});
export type PayrollConsoleSummary = z.infer<typeof payrollConsoleSummary>;

// The SSE stream the payroll console renders. The worker drives the browser and
// writes the pay runs; this stream relays its progress and terminal outcome.
export const payrollConsolePullEvent = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('log'), body: z.string() }),
  z.object({ kind: z.literal('session_started'), session_id: z.string() }),
  z.object({ kind: z.literal('status'), text: z.string() }),
  // A browser step, so the console can show Matilda working (latest screenshot +
  // a step log) without a full rrweb player.
  z.object({
    kind: z.literal('action'),
    row: z.object({
      step_number: z.number(),
      action_type: z.string(),
      page_url: z.string(),
      screenshot_url: z.string().nullable(),
    }),
  }),
  z.object({ kind: z.literal('needs_otp'), challenge: z.string().nullable() }),
  // saved_sign_in is the route's own answer, not the worker's: a pull that
  // carried typed credentials only stores them when no sign-in was there to
  // overwrite, and the console must not report a password it did not keep.
  z.object({
    kind: z.literal('summary'),
    summary: payrollConsoleSummary,
    saved_sign_in: z.boolean(),
  }),
  z.object({ kind: z.literal('failed'), reason: z.string() }),
  z.object({ kind: z.literal('error'), message: z.string() }),
]);
export type PayrollConsolePullEvent = z.infer<typeof payrollConsolePullEvent>;

// Lives here (not in `src/external/redis/`) so route.def files importing
// it don't pull ioredis into the browser bundle.
export const playwrightBusEvent = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('log'), body: z.string() }),
  z.object({ kind: z.literal('auth_required') }),
  z.object({
    kind: z.literal('session_started'),
    session_id: z.string(),
    stage_key: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('action'),
    row: z.object({
      step_number: z.number(),
      occurred_at: z.string(),
      action_type: z.string(),
      page_url: z.string(),
      selector: z.string().nullable(),
      payload: z.unknown(),
      notes: z.string().nullable(),
      screenshot_url: z.string().nullable(),
    }),
  }),
  z.object({
    kind: z.literal('download'),
    row: z.object({
      id: z.string(),
      suggested_filename: z.string(),
      source_url: z.string(),
      byte_size: z.number(),
      captured_at: z.string(),
    }),
  }),
  z.object({ kind: z.literal('assistant_text'), text: z.string() }),
  z.object({
    kind: z.literal('session_ended'),
    session_id: z.string(),
    status: z.enum(['completed', 'failed', 'aborted']),
    steps_executed: z.number(),
    output: z.record(z.string(), z.unknown()).nullable(),
    output_text: z.string(),
    failure_reason: z.string().nullable(),
  }),
  z.object({ kind: z.literal('rrweb'), payload: z.unknown() }),
  z.object({
    kind: z.literal('status'),
    status: z.enum(['running', 'completed', 'failed', 'aborted']),
    failure_reason: z.string().nullable(),
  }),
  // Terminal events for a console payroll pull: the worker publishes one of
  // these AFTER the browser session ends, once it has parsed the downloaded
  // report and written the pay runs (decoupled from the session lifecycle).
  z.object({ kind: z.literal('ingest_summary'), summary: payrollConsoleSummary }),
  z.object({ kind: z.literal('ingest_failed'), reason: z.string() }),
  z.object({ kind: z.literal('error'), message: z.string() }),
]);
export type PlaywrightBusEvent = z.infer<typeof playwrightBusEvent>;
